import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { ChartAsset } from '@lumen5/luminary/core/asset/ChartAsset';
import type {
    ChartConfig,
    ChartOptions,
    ChartSeriesType,
} from '@lumen5/luminary/core/asset/ChartAsset/types';

import type { ChartColorOverrides } from 'js/src/data/templates';
import { getChartColorOverrides } from 'js/src/data/templates';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import { setChartColorsAtPaths, shuffleChartPalette } from 'js/src/libs/chart-colors';
import { extractChartConfigToText } from 'js/src/libs/chart-data-extract';
import type { ChartTextGroupId, ChartTextStyleChange } from 'js/src/libs/chart-text-groups';
import { applyChartTextStyleChange } from 'js/src/libs/chart-text-groups';
import type { DefaultChartConfig } from 'js/src/libs/default-chart-configs';
import {
    CHART_SCENE_TILES,
    DEFAULT_BAR_CHART_CONFIGS,
    DEFAULT_LINE_CHART_CONFIGS,
    DEFAULT_PIE_CHART_CONFIGS,
} from 'js/src/libs/default-chart-configs';
import { generateChartConfig } from 'js/src/libs/generate-chart-config';
import { collectTemplateColorRefIndices, isNearWhiteColor } from 'js/src/libs/theme-ref-palette';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { getTemplateColorCodes } from 'js/src/luminary/luminary-selectors';
import { getOrCreateStickerCompatibleSceneOperation } from 'js/src/luminary/media-operations';

const CHART_TYPE_TO_CONFIGS: Record<string, DefaultChartConfig[]> = {
    bar: DEFAULT_BAR_CHART_CONFIGS,
    line: DEFAULT_LINE_CHART_CONFIGS,
    pie: DEFAULT_PIE_CHART_CONFIGS,
};

export function getDefaultChartConfig(
    chartType: string,
    configIndex: number,
): DefaultChartConfig | null {
    const configs = CHART_TYPE_TO_CONFIGS[chartType];
    if (!configs) return null;
    return configs[configIndex] ?? null;
}

type Position = { x: number; y: number };

// Inset on each side in reference-resolution units. Charts at full scene size feel
// cramped against the edges; this leaves a small margin matching the design's intent.
const CHART_EDGE_INSET = 20;

/**
 * EChartsPreview filters near-white template colors out of its palette so previews don't
 * render invisible bars on a white background. Luminary's chart renderer on the canvas uses
 * the *unfiltered* palette, so `templateColorByIndex(0)` in a config resolves to whatever sits
 * at index 0 of the template — which is often near-white. The result: previews look pink/red
 * but the chart on the scene is white.
 *
 * To match preview-to-scene, remap each theme-ref index in the config from its
 * filtered-palette position to the corresponding unfiltered position. Indices beyond the
 * filtered palette length wrap modulo the filtered length, mirroring the preview's wrap.
 *
 * `excludedIndices` lets callers reserve specific palette slots (e.g. the background color
 * for chart-scene previews) so the chart's bars/lines never resolve to that slot and blend
 * into the background.
 */
function buildThemeRefRemap(
    palette: readonly number[],
    excludedIndices: ReadonlySet<number> = new Set(),
): Map<string, string> {
    const usableIndices: number[] = [];
    for (let i = 0; i < palette.length; i++) {
        if (excludedIndices.has(i)) continue;
        if (isNearWhiteColor(palette[i])) continue;
        usableIndices.push(i);
    }
    const map = new Map<string, string>();
    if (usableIndices.length === 0) return map;
    for (let i = 0; i < palette.length; i++) {
        const filteredIdx = i % usableIndices.length;
        const targetIdx = usableIndices[filteredIdx];
        if (i !== targetIdx) {
            map.set(VideoData.templateColorByIndex(i), VideoData.templateColorByIndex(targetIdx));
        }
    }
    return map;
}

// Chart color overrides are authored against this canonical effective-slot space — the scene
// background slot (0) reserved — by getEffectiveChartColorIndices. applyChartColorRemaps keys
// overrides against the SAME space regardless of the caller's own excludedPaletteIndices, so a
// given chart series is recolored consistently no matter which entry point added the chart.
const CANONICAL_OVERRIDE_EXCLUDED_SLOTS: ReadonlySet<number> = new Set([0]);

/**
 * The distinct template color reference indices the chart scene tiles draw with (the `N` in every
 * `themeRef_colors_N`), sorted ascending.
 */
function getChartPaletteColorIndices(): number[] {
    const indices = new Set<number>();
    for (const { config } of CHART_SCENE_TILES) {
        collectTemplateColorRefIndices(config.echarts, indices);
    }
    return Array.from(indices).sort((a, b) => a - b);
}

/**
 * The template palette slots the chart scene tiles actually draw with, after the
 * near-white / background-slot remap that the scene charts apply (excluding slot 0, the scene
 * background). These are the colors shown in the template editor's chart color override utility
 * and the keys that chart color overrides are stored under.
 */
export function getEffectiveChartColorIndices(paletteColors: readonly number[]): number[] {
    const remap = buildThemeRefRemap(paletteColors, CANONICAL_OVERRIDE_EXCLUDED_SLOTS);
    const effective = new Set<number>();
    for (const originalIndex of getChartPaletteColorIndices()) {
        const mapped = remap.get(VideoData.templateColorByIndex(originalIndex));
        effective.add(mapped ? VideoData.getTemplateIndex(mapped) : originalIndex);
    }
    return Array.from(effective).sort((a, b) => a - b);
}

function remapThemeRefs<T>(node: T, mapping: Map<string, string>): T {
    if (mapping.size === 0) return node;
    if (typeof node === 'string') {
        return (mapping.get(node) ?? node) as unknown as T;
    }
    if (typeof node === 'function' || node === null || node === undefined) {
        return node;
    }
    if (Array.isArray(node)) {
        return node.map((item) => remapThemeRefs(item, mapping)) as unknown as T;
    }
    if (typeof node === 'object') {
        const result: Record<string, unknown> = {};
        for (const key of Object.keys(node as Record<string, unknown>)) {
            result[key] = remapThemeRefs((node as Record<string, unknown>)[key], mapping);
        }
        return result as T;
    }
    return node;
}

/**
 * Runs the full chart color pipeline over an echarts config and returns a new config whose theme
 * references are still `themeRef_colors_N` strings / hex literals — luminary (on the canvas) or
 * `resolveThemeRefs` (for previews) turns those into real colors.
 *
 * For each config color ref: if its canonical effective slot (the slot the override utility shows,
 * background reserved — see `getEffectiveChartColorIndices`) is overridden, the ref is replaced
 * with the override value directly so it lands exactly (a `themeRef_colors_M` value stays
 * `palette[M]`, a hex literal stays exact). Otherwise the ref gets this caller's near-white /
 * background remap. Keying overrides by the canonical slot — independent of the caller's
 * `excludedPaletteIndices` — means a given series is recolored the same whether the chart was
 * added via a scene selector, the elements tab, or drag-and-drop.
 */
export function applyChartColorRemaps<T>(
    echarts: T,
    palette: readonly number[],
    overrides: ChartColorOverrides | null | undefined,
    excludedPaletteIndices: ReadonlySet<number> = new Set(),
): T {
    const nearWhiteRemap = buildThemeRefRemap(palette, excludedPaletteIndices);
    const canonicalRemap = buildThemeRefRemap(palette, CANONICAL_OVERRIDE_EXCLUDED_SLOTS);

    const remap = new Map<string, string>();
    for (let i = 0; i < palette.length; i++) {
        const ref = VideoData.templateColorByIndex(i);
        const canonicalRef = canonicalRemap.get(ref) ?? ref;
        const overrideValue = overrides?.get(String(VideoData.getTemplateIndex(canonicalRef)));
        if (typeof overrideValue === 'string' && overrideValue.length > 0) {
            remap.set(ref, overrideValue);
        } else {
            const nearWhiteTarget = nearWhiteRemap.get(ref);
            if (nearWhiteTarget !== undefined) remap.set(ref, nearWhiteTarget);
        }
    }
    return remapThemeRefs(echarts, remap);
}

/**
 * Convenience wrapper around `applyChartColorRemaps` for luminary operations: pulls the template
 * palette and the template's chart color overrides from the current luminary / redux state.
 */
export function remapChartColors<T>(
    luminary: Readonly<Luminary>,
    getState: GetRootState,
    echarts: T,
    excludedPaletteIndices: ReadonlySet<number> = new Set(),
): T {
    return applyChartColorRemaps(
        echarts,
        getTemplateColorCodes(luminary),
        getChartColorOverrides(getState(), luminary.template.id),
        excludedPaletteIndices,
    );
}

type AddChartAssetOptions = {
    excludedPaletteIndices?: readonly number[];
};

export type AddChartAssetResult = { sceneId: string; assetId: string };

export const addChartAssetToSceneOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        sceneId: string,
        chartType: string,
        config: DefaultChartConfig,
        position?: Position,
        options?: AddChartAssetOptions,
    ): AddChartAssetResult | null => {
        const scene = call(getOrCreateStickerCompatibleSceneOperation, sceneId);
        if (!scene) return null;

        const excluded = new Set(options?.excludedPaletteIndices ?? []);
        const remappedEcharts = remapChartColors(luminary, getState, config.echarts, excluded);

        let result: AddChartAssetResult | null = null;

        luminary.batchRun(() => {
            const newAsset = scene.addSticker(VideoData.CHART_ASSET_TYPE);
            const { width: refWidth, height: refHeight } = scene.root.referenceResolution;
            newAsset.width = refWidth - CHART_EDGE_INSET * 2;
            newAsset.height = refHeight - CHART_EDGE_INSET * 2;
            newAsset.transformation.position.x = position ? position.x : refWidth / 2;
            newAsset.transformation.position.y = position ? position.y : refHeight / 2;
            newAsset.transformation.anchor.x = 0.5;
            newAsset.transformation.anchor.y = 0.5;
            newAsset.chartType = chartType as ChartAsset['chartType'];
            const chartConfig: ChartConfig = {
                echarts: remappedEcharts as unknown as ChartOptions,
                designResolution: config.designResolution,
            };
            newAsset.config = chartConfig;
            newAsset.chartData = extractChartConfigToText(remappedEcharts);
            result = { sceneId: scene.id, assetId: newAsset.id };
        });

        return result;
    },
);

/**
 * Turn the given scene into a "chart scene": clears any default-design assets (media slots,
 * text placeholders), paints the background with the first palette color, then adds the
 * chart sticker. Used both for the chart-preview tiles in the Add Scene modal and for the
 * scene that's actually inserted when the user picks a chart preview.
 */
export const setupChartSceneOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneId: string,
        chartType: string,
        config: DefaultChartConfig,
    ): AddChartAssetResult | null => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) return null;

        // addChartAssetToScene may redirect the chart into a different scene (e.g. a new
        // overlay) when the original scene can't host a sticker — so return the actual
        // landing sceneId, not the requested one.
        let result: AddChartAssetResult | null = null;
        luminary.batchRun(() => {
            scene.getAssets().forEach((asset) => asset.destroy());

            const colorAsset = scene.addAsset(VideoData.COLOR_ASSET_TYPE, {
                name: 'chart_background',
            });
            colorAsset.sourceColor = VideoData.templateColorByIndex(0) as unknown as number;

            result = call(addChartAssetToSceneOperation, sceneId, chartType, config, undefined, {
                excludedPaletteIndices: [0],
            });
        });
        return result;
    },
);

export type ApplyGeneratedChartConfigStatus = 'no-asset' | 'success' | 'error';

type ApplyGeneratedChartConfigArgs = {
    sceneId: string;
    assetId: string;
    chartType: string;
    data: string;
    baseConfigOverride?: ChartOptions;
    // When true (popover's first generation), write `chartData` from the
    // newly generated config so the Chart Settings textarea reflects the
    // actual chart values. When false (Chart Settings regenerate), preserve
    // the user's typed text verbatim so editing-and-regenerating is stable.
    extractChartDataFromResult?: boolean;
    systemTriggered?: boolean;
    parentActionGroup?: string;
};

export const applyGeneratedChartConfigOperation = createLuminaryOperation(
    async (
        { luminary, call, getState },
        {
            sceneId,
            assetId,
            chartType,
            data,
            baseConfigOverride,
            extractChartDataFromResult,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: ApplyGeneratedChartConfigArgs,
    ): Promise<ApplyGeneratedChartConfigStatus> => {
        const initialScene = luminary.getSceneById(sceneId);
        if (!initialScene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE)) {
            return 'no-asset';
        }

        const sceneText = initialScene
            .getAssets()
            .filter(VideoData.isTextAsset)
            .map((a) => a.text.trim())
            .filter(Boolean)
            .join('\n');

        const baseConfigEcharts = baseConfigOverride
            ? (remapChartColors(luminary, getState, baseConfigOverride) as ChartOptions)
            : initialScene.getAsset(assetId, VideoData.CHART_ASSET_TYPE)?.config?.echarts;

        try {
            const llmConfig = await generateChartConfig(sceneText, {
                chartType,
                data,
                baseConfig: baseConfigEcharts,
            });

            const scene = luminary.getSceneById(sceneId);
            const asset = scene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE);
            if (!asset) {
                return 'no-asset';
            }

            const nextEcharts = (llmConfig?.echarts ??
                asset.config?.echarts) as unknown as ChartOptions;

            luminary.batchRun(() => {
                asset.chartData = extractChartDataFromResult
                    ? extractChartConfigToText(nextEcharts)
                    : data;
                asset.config = {
                    ...asset.config,
                    echarts: nextEcharts,
                };
            });

            call(checkpointVideoOperation, { systemTriggered, parentActionGroup });

            return 'success';
        } catch {
            return 'error';
        }
    },
);

export const setChartTypeOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneId: string,
        assetId: string,
        chartType: ChartSeriesType,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ): void => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE);
        if (!asset) return;
        asset.chartType = chartType;
        call(checkpointVideoOperation, { systemTriggered, parentActionGroup });
    },
);

export const setChartColorsAtPathsOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneId: string,
        assetId: string,
        paths: string[][],
        newColor: string,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ): void => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE);
        if (!asset || paths.length === 0) return;
        const currentEcharts = (asset.config?.echarts ?? {}) as Record<string, unknown>;
        const updated = setChartColorsAtPaths(currentEcharts, paths, newColor);
        luminary.batchRun(() => {
            asset.config = {
                ...asset.config,
                echarts: updated as unknown as ChartOptions,
            };
        });
        call(checkpointVideoOperation, { systemTriggered, parentActionGroup });
    },
);

export const updateChartTextStyleOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneId: string,
        assetId: string,
        groupId: ChartTextGroupId,
        change: ChartTextStyleChange,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ): void => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE);
        if (!asset) return;
        const currentEcharts = (asset.config?.echarts ?? {}) as Record<string, unknown>;
        const updated = applyChartTextStyleChange(currentEcharts, groupId, change);
        if (updated === currentEcharts) return;
        luminary.batchRun(() => {
            asset.config = {
                ...asset.config,
                echarts: updated as unknown as ChartOptions,
            };
        });
        call(checkpointVideoOperation, { systemTriggered, parentActionGroup });
    },
);

type ChartAssetEdgeResizeArgs = {
    sceneId: string;
    assetId: string;
    edge: 'left' | 'right' | 'top' | 'bottom';
    delta: number;
    startWidth: number;
    startHeight: number;
    startCenterX: number;
    startCenterY: number;
    minSize?: number;
};

export const resizeChartAssetByEdgeOperation = createLuminaryOperation(
    ({ luminary }, args: ChartAssetEdgeResizeArgs): void => {
        const scene = luminary.getSceneById(args.sceneId);
        const asset = scene?.getAsset(args.assetId, VideoData.CHART_ASSET_TYPE);
        if (!asset) return;

        const minSize = args.minSize ?? 50;
        const isHorizontal = args.edge === 'left' || args.edge === 'right';
        const widthSign = args.edge === 'right' || args.edge === 'bottom' ? 1 : -1;

        luminary.batchRun(() => {
            if (isHorizontal) {
                const newWidth = Math.max(minSize, args.startWidth + widthSign * args.delta);
                // Drive position from the *effective* size change, not from raw `args.delta`:
                // once the size clamps at `minSize`, raw delta keeps growing but the chart
                // can't shrink further. Without this, the anchored (opposite) edge would
                // slide instead of staying put when the user drags past the min.
                const effectiveDelta = widthSign * (newWidth - args.startWidth);
                asset.maxWidth = newWidth;
                asset.transformation.position.x = args.startCenterX + effectiveDelta / 2;
            } else {
                const newHeight = Math.max(minSize, args.startHeight + widthSign * args.delta);
                const effectiveDelta = widthSign * (newHeight - args.startHeight);
                asset.maxHeight = newHeight;
                asset.transformation.position.y = args.startCenterY + effectiveDelta / 2;
            }
        });
    },
);

export const shuffleChartPaletteOperation = createLuminaryOperation(
    (
        { luminary, call },
        sceneId: string,
        assetId: string,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ): void => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAsset(assetId, VideoData.CHART_ASSET_TYPE);
        if (!asset) return;
        const currentEcharts = (asset.config?.echarts ?? {}) as Record<string, unknown>;
        const templateColorCount = getTemplateColorCodes(luminary).length;
        const updated = shuffleChartPalette(currentEcharts, templateColorCount);
        if (updated === currentEcharts) return;
        luminary.batchRun(() => {
            asset.config = {
                ...asset.config,
                echarts: updated as unknown as ChartOptions,
            };
        });
        call(checkpointVideoOperation, { systemTriggered, parentActionGroup });
    },
);
